import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { Like } from '@modules/common/types/like';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { ProjectComponent } from '@modules/elements/components/project/project.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { Project } from '@modules/tasks/types/project';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { Task } from '@modules/tasks/types/task';
import { isStitchBoundaryFloating } from '@modules/tasks/utils/stitch-time.util';
import { normalizeAllDayDate } from '@sharedlib/common/utils/date';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-projects-list',
  templateUrl: './projects-list.component.html',
  styleUrls: ['./projects-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class ProjectsListComponent extends BaseStitchListComponent<Project> implements OnInit, OnDestroy, OnChanges {
  /** Owner carrier for embedded item-owned lists; ordinary app lists keep their selection. */
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();
  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Like<ProjectsFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickProject = new EventEmitter<Project>();
  @Output()
  public readonly openProject = new EventEmitter<Project>();
  @Output()
  public readonly doubleClickProject = new EventEmitter<Project>();
  @Output()
  public readonly openTask = new EventEmitter<Task>();

  @ViewChildren(ProjectComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<Project>[] = [];

  private projectsService = inject(ProjectsService);
  private tasksStateService = inject(TaskingAppStateService);
  private linkedInfoService = inject(LinkedInfoService);

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    return [
      ...new Set([
        ...super.stitchSignalSpaceIds(),
        ...(this.tenantItems() ?? []).flatMap(({ accountId }) => (accountId ? [accountId] : [])),
      ]),
    ];
  }

  protected override getStitchType(): StitchType {
    return StitchType.PROJECT;
  }

  public override ngOnInit() {
    merge(
      this.tasksStateService.getRefreshAll(),
      this.linkedInfoService.getRefresh(),
      this.projectsService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes || 'tenantItems' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Project[]; count: number }> {
    return this.projectsService.search(
      { ...this.filters, limit, offset },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    );
  }

  public handleDoubleClickItem(project: Project) {
    this.doubleClickProject.emit(project);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Project>): void {
    this.clickProject.emit(event.item.data);
  }

  protected override getSectionValue(item: Project): unknown | undefined {
    if (this.filters.sortBy === 'priority') {
      return item.priority;
    }
    if (this.filters.sortBy === 'date') {
      return item.startDateTime ? this.getDateSectionValue(item, item.startDateTime) : undefined;
    }
    if (this.filters.sortBy === 'due-date') {
      return item.endDateTime ? this.getDateSectionValue(item, item.endDateTime) : undefined;
    }
    return super.getSectionValue(item);
  }

  /**
   * SBUX-829: a FLOATING boundary is a timezone-agnostic calendar date stored at UTC midnight — read
   * its stored day directly instead of projecting it, or an account west of UTC files it a day early.
   * A TIMED boundary is a real instant and takes the base projection.
   *
   * Floating-ness is a property of the FIELD being grouped, not of the item — see the same override in
   * `TasksListComponent`. Sorting by "Created" groups on `createdAt`, a real instant even for a project
   * with no start time; normalizing it as if floating named the instant's UTC day in the heading.
   */
  protected override getDateSectionValue(item: Project, value: Date): Date {
    const boundary = this.sectionBoundary();
    return boundary && isStitchBoundaryFloating(item, boundary)
      ? normalizeAllDayDate(value)
      : super.getDateSectionValue(item, value);
  }

  /**
   * Which item boundary the current sort field reads, or `null` when the field is a real instant
   * (`createdAt`) and therefore can never be floating. `title`, `priority` and `score` never yield a
   * `Date`, so they never get here.
   */
  private sectionBoundary(): 'start' | 'end' | null {
    if (this.filters.sortBy === 'date') {
      return 'start';
    }
    if (this.filters.sortBy === 'due-date') {
      return 'end';
    }
    return null;
  }
}
